import { useCallback, useMemo } from 'react';
import { ProductType } from 'types';
import { TraceProvider } from '../../components/trace/TraceProvider';
import { useTraceConfig } from '../../hooks/trace/useTraceConfig';
import { usePersistedState } from '../../hooks/usePersistedState';
import { TraceConfig } from '../../types/trace';
import { Trace } from './Trace';

interface TraceStandaloneWrapperProps {
  productType: ProductType;
}

type PersistableTraceConfig = Omit<TraceConfig, 'date'>;

/**
 * Standalone Trace wrapper used by /trace and the Intraday OI tab.
 * Supplies TraceProvider with nuqs-backed config + localStorage shadow.
 *
 * `date` is intentionally excluded from the localStorage shadow so the
 * Trace page always defaults to today on a fresh load. (URL persistence
 * is also disabled at the parser level — see useTraceConfig.)
 */
export const TraceStandaloneWrapper = ({
  productType,
}: TraceStandaloneWrapperProps) => {
  const [config, updateConfig] = useTraceConfig('tr');

  const persistableConfig = useMemo<PersistableTraceConfig>(() => {
    const { date: _date, ...rest } = config;
    return rest;
  }, [config]);

  const restore = useCallback(
    (saved: PersistableTraceConfig) => {
      updateConfig(saved);
    },
    [updateConfig],
  );

  usePersistedState('tr_state', persistableConfig, restore, ['tr']);

  return (
    <TraceProvider
      mode="standalone"
      productType={productType}
      config={config}
      onConfigChange={updateConfig}
    >
      <Trace productType={productType} />
    </TraceProvider>
  );
};
